import styled from "styled-components"

import { spacing } from "@minuthq/meatball-ui-react"

import { TTimezone } from "src/data/timezones/timezones"
import { Container } from "src/ui/Container/Container"
import { DatePickerDropdown } from "src/ui/DateRangePicker/DatePickerDropdown"
import { DateRangePickerContainerProps } from "src/ui/DateRangePicker/DateRangePickerContainer"
import {
  isDateBlocked,
  isTimeBlocked,
  useDateTimePickerStateWithTimezone,
} from "src/ui/MDateTimePicker/dateTimePickerUtils"
import {
  MTimePickerInput,
  TMTimePickerInputProps,
} from "src/ui/MTimePickerInput/MTimePickerInput"

export type TDateTimePickerProps = {
  initialDate?: Date | null
  datePickerProps?: Partial<DateRangePickerContainerProps>
  datePickerLabel?: string
  timePickerLabel?: string
  timePickerInputProps?: Partial<TMTimePickerInputProps>
  required?: boolean
  onChange: (date: Date | null) => void
  disabled?: boolean
  isDateTimeBlocked?: (date: Date) => boolean
  timezone?: string | TTimezone
}

export function MDateTimePicker({
  initialDate,
  datePickerProps,
  datePickerLabel,
  timePickerLabel,
  timePickerInputProps,
  isDateTimeBlocked,
  required,
  onChange,
  disabled,
  timezone,
}: TDateTimePickerProps) {
  const {
    dateInLocalTime,
    setDateInLocalTime,
    timeInLocalTimezone,
    setTimeInLocalTimezone,
  } = useDateTimePickerStateWithTimezone(onChange, initialDate, timezone)

  const isTimeBlockedFn = isTimeBlocked(
    dateInLocalTime,
    isDateTimeBlocked,
    timezone
  )
  const isDateBlockedFn = isDateBlocked(
    timeInLocalTimezone,
    isDateTimeBlocked,
    timezone
  )

  return (
    <Container>
      <Box>
        <DatePickerDropdown
          label={datePickerLabel}
          date={dateInLocalTime}
          onChange={setDateInLocalTime}
          dateRangePickerProps={{
            futureDatesSelectable: true,
            dateRangeProps: {
              isDateBlocked: isDateBlockedFn,
              ...datePickerProps?.dateRangeProps,
            },
            ...datePickerProps,
          }}
          required={required}
          disabled={disabled}
        />

        <MTimePickerInput
          initialValue={timeInLocalTimezone}
          label={timePickerLabel}
          onChange={setTimeInLocalTimezone}
          required={required}
          disabled={disabled}
          isTimeBlocked={isTimeBlockedFn}
          {...timePickerInputProps}
        />
      </Box>
    </Container>
  )
}

const Box = styled.div`
  display: grid;
  gap: ${spacing.M};

  @container (width > 500px) {
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  }

  @container (width > 700px) {
    grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  }
`
